import { Color } from "@tiptap/extension-color";

import Blockquote from "@tiptap/extension-blockquote";
import Highlight from "@tiptap/extension-highlight";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { BubbleMenu, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useImperativeHandle, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { IconDispatcher } from "shared/Icons";
import "./editor.scss";
import { AddMentionConfig, ColorsOptions, InsertOptions, LinkOptions, TextAlignOptions } from "./extensions";
import { CustomParagraph, handlePaste } from "./helpers";
import { AIOptions } from "./extensions/ai-options";
import styled from "styled-components";
import { t } from "i18next";
import { Spinner } from "reactstrap";
import { useLocation } from "react-router-dom";

const extensions = [
	StarterKit.configure({
		history: { depth: 10 },
	}),
	CustomParagraph,
	TextAlign.configure({
		types: ["heading", "paragraph", "listItem"],
		alignments: ["left", "center", "right", "justify"],
	}),
	Highlight.configure({ multicolor: true }),
	Link.configure({
		HTMLAttributes: {
			target: "_blank",
			rel: "noopener noreferrer",
		},
	}),
	TaskList,
	TaskItem.configure({
		nested: true,
	}),
	Blockquote,
	TextStyle,
	Color,
	HorizontalRule,
];

export default function Editor({ value, onUpdate, disabled, ...props }) {
	const [portalContent, setPortalContent] = useState(null);
	const [, forceUpdate] = useState(false);
	const { pathname } = useLocation();
	const hasFocused = useRef(false); // Prevents multiple re-renders

	const alexLoading = useRef({
		loading: false,
		typing: false,
	});
	const toolbarRef = useRef(null);

	const Mention = props.mentionFunction ? AddMentionConfig({ setPortalContent, setMentionElement: props.mentionFunction }) : null;
	const PlaceholderExtension = props.placeholder
		? Placeholder.configure({
			placeholder: props.placeholder,
			showOnlyWhenEditable: true,
			showOnlyCurrent: false,
		})
		: null;

	const editor = useEditor(
		{
			extensions: [...extensions, Mention, PlaceholderExtension],
			content: value,
			editable: !disabled,
			onUpdate: ({ editor }) => {
				if (props?.onChange) {
					props.onChange(editor.getHTML());
				}
			},
			onBlur({ editor, ...props }) {
				if (toolbarRef.current?.hide) {
					toolbarRef.current.hide(props.event);
				}
				if (onUpdate) {
					onUpdate(editor.getHTML());
				}
			},
			editorProps: {
				handlePaste: handlePaste,
			},
		},
		[value, disabled, props.onChange, onUpdate]
	);

	useEffect(() => {
		if (props.autoFocus && editor && !hasFocused.current) {
			editor.commands.focus();
			const endPos = editor.state.doc.content.size;
			editor.commands.setTextSelection(endPos);
			hasFocused.current = true; // Ensures it runs only once
		}
	}, [props.autoFocus, editor]);
	return (
		<>
			{alexLoading.current.loading || alexLoading.current.typing ? (
				<AlexLoading pathname={pathname}>
					<IconDispatcher type="SPARKLES" />
					{t(alexLoading.current.loading ? "ALEX_IS_THINKING" : "ALEX_IS_TYPING")}
					{alexLoading.current.loading && (
						<Spinner
							type="grow"
							color="#c4c4c4"
							style={{
								height: "0.5rem",
								width: "0.5rem",
							}}
						/>
					)}
				</AlexLoading>
			) : null}
			<EditorContent editor={editor} className="editor-content" />
			{portalContent && createPortal(portalContent, document.body)}
			{!props.hideToolbar && <ToolBar editor={editor} toolbarRef={toolbarRef} alexLoading={alexLoading} forceUpdate={forceUpdate} />}
		</>
	);
}

const ToolBar = ({ editor, toolbarRef, alexLoading, forceUpdate }) => {
	const colorRef = useRef(null);
	const insertRef = useRef(null);
	const textAlignRef = useRef(null);
	const linkRef = useRef(null);
	const aiRef = useRef(null);

	useImperativeHandle(toolbarRef, () => {
		return {
			hide: (e) => {
				colorRef.current?.hide(e);
				insertRef.current?.hide(e);
				textAlignRef.current?.hide(e);
				linkRef.current?.hide(e);
				aiRef.current?.hide(e);
			},
		};
	});

	if (!editor) {
		return null;
	}

	const isActive = (type, att) => {
		if (att) return editor.isActive(type, att) ? "is-active" : "";
		return editor.isActive(type) ? "is-active" : "";
	};
	const handleClick = (command, event) => {
		if (event) event.preventDefault();
		editor.chain().focus()[command]().run();
	};

	const handleshow = (e, type) => {
		switch (type) {
			case "color":
				colorRef.current?.toggle(e);
				insertRef.current?.hide(e);
				textAlignRef.current?.hide(e);
				linkRef.current?.hide(e);
				aiRef.current?.hide(e);

				break;
			case "insert":
				insertRef.current?.toggle(e);
				colorRef.current?.hide(e);
				textAlignRef.current?.hide(e);
				linkRef.current?.hide(e);
				aiRef.current?.hide(e);

				break;
			case "textAlign":
				textAlignRef.current?.toggle(e);
				colorRef.current?.hide(e);
				insertRef.current?.hide(e);
				linkRef.current?.hide(e);
				aiRef.current?.hide(e);
				break;
			case "link":
				linkRef.current?.toggle(e);
				colorRef.current?.hide(e);
				insertRef.current?.hide(e);
				textAlignRef.current?.hide(e);
				aiRef.current?.hide(e);
				break;
			case "ai":
				aiRef.current?.toggle(e);
				colorRef.current?.hide(e);
				insertRef.current?.hide(e);
				textAlignRef.current?.hide(e);
				linkRef.current?.hide(e);
				break;
			default:
				break;
		}
	};
	if (editor.getAttributes("link").href && editor.isActive("link")) {
		return (
			<BubbleMenu editor={editor} className="bubble-menu">
				<LinkOptions editor={editor} linkRef={linkRef} handleshow={handleshow} />
			</BubbleMenu>
		);
	}

	return (
		<BubbleMenu editor={editor} tippyOptions={{ interactive: true, duration: 200, hideOnClick: true }}>
			<div className="bubble-menu">
				<AIOptions editor={editor} insertRef={aiRef} handleshow={handleshow} alexLoading={alexLoading} forceUpdate={forceUpdate} />
				<InsertOptions editor={editor} insertRef={insertRef} handleshow={handleshow} />
				<ColorsOptions editor={editor} colorRef={colorRef} handleshow={handleshow} />
				<button title="Bold" onClick={(e) => handleClick("toggleBold", e)} className={isActive("bold")}>
					{IconDispatcher("bold")}
				</button>
				<button title="Italic" onClick={(e) => handleClick("toggleItalic", e)} className={isActive("italic")}>
					{" "}
					{IconDispatcher("italic")}
				</button>
				<button title="Strike" onClick={(e) => handleClick("toggleStrike", e)} className={isActive("strike")}>
					{" "}
					{IconDispatcher("strikethrough")}
				</button>
				<button title="Clean format" onClick={(e) => handleClick("unsetAllMarks", e)}>
					{" "}
					{IconDispatcher("text-slash")}
				</button>
				<button title="Undo" onClick={(e) => handleClick("undo", e)} disabled={!editor.can().undo()}>
					{IconDispatcher("undo")}
				</button>
				<button title="Redo" onClick={(e) => handleClick("redo", e)} disabled={!editor.can().redo()}>
					{IconDispatcher("redo")}
				</button>
				<TextAlignOptions editor={editor} textAlignRef={textAlignRef} handleshow={handleshow} />
				<LinkOptions editor={editor} linkRef={linkRef} handleshow={handleshow} />
			</div>
		</BubbleMenu>
	);
};

const AlexLoading = styled.div`
	position: absolute;
	top: 44px;
	right: 15px;
	font-size: 12px;
	font-style: italic;
	color: #c4c4c4;
	font-family: "Inter";
	font-size: 12px;
	font-style: normal;
	font-weight: 400;
	display: flex;
	align-items: center;
	gap: 5px;
	${({ pathname }) => {
		switch (pathname) {
			case "/EditMap":
				return `
              top: -20px;

              `;
			default:
				return "";
		}
	}}
`;
